import {
  NetworkStatus,
  useNetworkStatus,
} from "$global-providers/network-status-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { TelephoneRequestModal } from "$routes/_lib/telephone-request-modal/telephone-request-modal";
import { QUERY as USE_UI_DEFAULT_QUERY } from "$routes/user/_lib/use-ui-default";
import { gql } from "@apollo/client";
import React from "react";
import { useSessionStorage } from "usehooks-ts";
import {
  useGetUserWhatsappConsentQuery,
  useUpdateWhatsappConsentMutation,
} from "./__generated__/existing-user-whatsapp-consent-overlay.gql";
import {
  useExistingUserWhatsappConsentOverlayUiDefaults,
  whatsappConsentUiDefaultVariables,
} from "./use-existing-user-whatsapp-consent-overlay-ui-defaults";
import { WhatsappConsentModal } from "./whatsapp-consent-modal";

/**
 * By virtue of this mutation fetching the user and it's ID, it will update the user's own
 * entity in the apollo cache, thereby triggering the query hook to re-run and the overlay
 * to disappear.
 */
gql`
mutation UpdateWhatsappConsent($userId: String!, $consented: Boolean!, $uiDefaultArgs: UpsertUiDefaultInput!) {
  updateUser(input: {_id: $userId, person: {whatsappConsent: $consented}}) {
    _id
    person {
      whatsappConsent
    }
  }
  upsertUiDefault(input: $uiDefaultArgs) {
    id
    namespace
    name
    scope
    value
  }
}`;

gql`
  query GetUserWhatsappConsent {
    myUser {
      _id
      person {
        whatsappConsent
      }
    }
  }
`;

const Feature = () => {
  const { id: userId } = useUser();
  const { networkStatus } = useNetworkStatus();
  const isOffline = networkStatus === NetworkStatus.OFFLINE;
  const [step, setStep] = React.useState(0);
  // Ideally this would be on the apollo cache using update but can't get it to work and pushed for time
  const [offlineHasSeenModal, setOfflineHasSeenModal] = useSessionStorage(
    "offlineHasSeenModal",
    false,
  );
  const { data: userWhatsappConsentData, loading: userWhatsappConsentLoading } =
    useGetUserWhatsappConsentQuery();
  const {
    value: overlayUiDefault,
    queryLoading: overlayUiDefaultLoading,
    save,
  } = useExistingUserWhatsappConsentOverlayUiDefaults();

  const [updateWhatsappConsent, { loading: updateWhatsappConsentLoading }] =
    useUpdateWhatsappConsentMutation({
      onError: (error) => {
        console.error(error);
        toast.error(error.message);
      },
      update: (cache, { data }) => {
        cache.writeQuery({
          query: USE_UI_DEFAULT_QUERY,
          variables: { args: whatsappConsentUiDefaultVariables },
          data: { UiDefault: data.upsertUiDefault },
        });
      },
    });

  if (isOffline || userWhatsappConsentLoading || overlayUiDefaultLoading) {
    return null;
  }

  const isHidden =
    offlineHasSeenModal ||
    !!userWhatsappConsentData?.myUser?.person?.whatsappConsent ||
    overlayUiDefault?.hasSeen;

  return (
    <>
      <TelephoneRequestModal
        open={!isHidden && step === 0}
        onSkip={() => setStep(1)}
        onCompleted={() => setStep(1)}
      />
      <WhatsappConsentModal
        open={!isHidden && step === 1}
        onSubmit={(consented) =>
          updateWhatsappConsent({
            variables: {
              userId,
              consented,
              uiDefaultArgs: {
                ...whatsappConsentUiDefaultVariables,
                // Set this to true irrespective of the user's choice, as we still want to hide it for users
                // who choose not to consent
                value: { hasSeen: true },
              },
            },
          })
        }
        onClose={() => {
          setOfflineHasSeenModal(true);
          save(true);
        }}
        submitLoading={updateWhatsappConsentLoading}
      />
    </>
  );
};

export const ExistingUserWhatsappConsentOverlay: FC = () => {
  return <Feature />;
};
